import { AnimatePresence, motion } from 'framer-motion';
import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react';
import React, { useEffect } from 'react';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string | React.ReactNode;
  type?: 'success' | 'error' | 'info' | 'warning';
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  actions?: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
  type = 'info',
  showCloseButton = true,
  closeOnOverlayClick = true,
  actions,
}) => {
  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const getIcon = () => {
    const iconClass = 'w-6 h-6';
    switch (type) {
      case 'success':
        return <CheckCircle className={`${iconClass} text-green-600`} />;
      case 'error':
        return <AlertCircle className={`${iconClass} text-red-600`} />;
      case 'warning':
        return <AlertTriangle className={`${iconClass} text-yellow-600`} />;
      default:
        return <Info className={`${iconClass} text-blue-600`} />;
    }
  };

  const getColorClasses = () => {
    switch (type) {
      case 'success':
        return {
          border: 'border-green-200',
          bg: 'bg-green-50',
          title: 'text-green-900',
          message: 'text-green-800',
        };
      case 'error':
        return {
          border: 'border-red-200',
          bg: 'bg-red-50',
          title: 'text-red-900',
          message: 'text-red-800',
        };
      case 'warning':
        return {
          border: 'border-yellow-200',
          bg: 'bg-yellow-50',
          title: 'text-yellow-900',
          message: 'text-yellow-800',
        };
      default:
        return {
          border: 'border-blue-200',
          bg: 'bg-blue-50',
          title: 'text-blue-900',
          message: 'text-blue-800',
        };
    }
  };

  const colors = getColorClasses();

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeOnOverlayClick ? onClose : undefined}
            className='fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4'
          >
            {/* Modal */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', duration: 0.3 }}
              onClick={e => e.stopPropagation()}
              className='glass-effect rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-hidden'
            >
              {/* Header with hexagon accent */}
              <div
                className={`relative ${colors.bg} ${colors.border} border-b p-6`}
              >
                <div className='flex items-start justify-between relative z-10'>
                  <div className='flex items-center space-x-3 flex-1'>
                    <div className='flex-shrink-0'>{getIcon()}</div>
                    <h3 className={`text-xl font-bold ${colors.title} flex-1`}>
                      {title}
                    </h3>
                  </div>
                  {showCloseButton && (
                    <button
                      onClick={onClose}
                      className='flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-lg hover:bg-white/50 relative z-20'
                      aria-label='Close modal'
                    >
                      <X className='w-5 h-5' />
                    </button>
                  )}
                </div>
                {/* Hexagon accent decoration - positioned to avoid close button */}
                <div className='absolute top-0 left-0 w-20 h-20 opacity-10 pointer-events-none z-0'>
                  <div className='hexagon bg-gradient-to-br from-hexagon to-hexagon-dark w-full h-full' />
                </div>
              </div>

              {/* Content */}
              <div className='p-6 overflow-y-auto max-h-[calc(90vh-200px)]'>
                <div className={`${colors.message} whitespace-pre-wrap`}>
                  {typeof message === 'string' ? (
                    <p className='leading-relaxed'>{message}</p>
                  ) : (
                    <div className='leading-relaxed'>{message}</div>
                  )}
                </div>
              </div>

              {/* Footer */}
              {(actions || !showCloseButton) && (
                <div className='px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-3'>
                  {actions || (
                    <button
                      onClick={onClose}
                      className='hexagon-button px-6 py-2 text-sm'
                    >
                      Close
                    </button>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default Modal;
